import { Fragment } from 'preact';
import {
  ACADEMIC_WEEKS,
  DAYS,
  closureForDate,
  weekHolidayLabel,
  type DayIndex,
} from '../lib/calendar.ts';
import { addDays, frDayDate, frWeekRange, parseLocalDate } from '../lib/dates.ts';
import { RITUAL_END, RITUAL_START, RITUAL_TITLE, SLOTS } from '../lib/slots.ts';
import { LEGEND_SUBJECTS, subjectColor } from '../lib/subjects.ts';
import { live, useStore } from '../store/store.ts';
import type { Lesson, SlotIndex } from '../store/types.ts';
import { openModal, toast } from '../ui/ui-store.ts';
import { CopyDayModal } from '../features/lesson/CopyDayModal.tsx';
import { LessonModal } from '../features/lesson/LessonModal.tsx';
import { PrepWeekModal } from '../features/lesson/PrepWeekModal.tsx';
import { SCHOOL_YEAR_LABEL } from '../data/school-year-2026-2027.ts';

function openLesson(lessonId: string | null, day: DayIndex, slot: SlotIndex) {
  openModal(
    lessonId ? 'Modifier l’activité' : 'Ajouter une activité',
    <LessonModal lessonId={lessonId} initialDay={day} initialSlot={slot} />,
  );
}

/** One filled cell of the grid. */
function LessonCard({ lesson }: { lesson: Lesson }) {
  const isRitual = lesson.slot === -1;

  return (
    <div
      class={isRitual ? 'lesson ritualLesson' : 'lesson'}
      style={isRitual ? undefined : { background: lesson.color }}
    >
      <div>
        <div class="sub">{lesson.domain || lesson.subject}</div>
        {lesson.lessonNumber && <span class="lessonReference">N° {lesson.lessonNumber}</span>}
        <b>{lesson.title}</b>
        {lesson.detail && <div class="lessonDetail">{lesson.detail}</div>}
        {lesson.material && (
          <div class="lessonExtra">
            <span>📦 {lesson.material}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function WeekPage() {
  const currentWeekIndex = useStore((s) => s.currentWeekIndex);
  const setWeekIndex = useStore((s) => s.setWeekIndex);
  const moveWeek = useStore((s) => s.moveWeek);
  const goToTodayWeek = useStore((s) => s.goToTodayWeek);
  const lessons = useStore((s) => s.lessons);

  const weekStart = ACADEMIC_WEEKS[currentWeekIndex] ?? ACADEMIC_WEEKS[0]!;
  const monday = parseLocalDate(weekStart);
  const dates = DAYS.map((_, i) => addDays(monday, i));

  // Recomputed per render rather than cached: the grid is small and this keeps
  // the source of truth in one place.
  const weekLessons = live(lessons).filter((l) => l.weekStart === weekStart);
  const lessonAt = (day: number, slot: number): Lesson | undefined =>
    weekLessons.find((l) => l.day === day && l.slot === slot);

  const holidayLabel = weekHolidayLabel(weekStart);

  function withTemplate(action: 'save' | 'load') {
    if (action === 'save') {
      const n = useStore.getState().saveWeekTemplate();
      toast(n ? 'Semaine type mémorisée' : 'Cette semaine est vide');
      return;
    }

    if (!live(useStore.getState().weekTemplate).length) {
      toast('Aucune semaine type enregistrée');
      return;
    }
    if (!confirm('Remplacer le contenu de cette semaine par ta semaine type ?')) return;

    const n = useStore.getState().loadWeekTemplate();
    toast(n ? 'Semaine type chargée' : 'Aucun jour disponible cette semaine');
  }

  return (
    <section class="page printWeek">
      <div class="sectionTitle">
        <div>
          <h1>Semainier</h1>
          <p class="muted">
            Année scolaire {SCHOOL_YEAR_LABEL} • les dates et congés sont déjà intégrés.
          </p>
        </div>
        <button class="btn primary" onClick={() => openLesson(null, 0, 0)}>
          + Ajouter une activité
        </button>
      </div>

      <div class="weekYearBar">
        <button class="btn" onClick={() => moveWeek(-1)} aria-label="Semaine précédente">
          ←
        </button>

        <select
          value={String(currentWeekIndex)}
          onChange={(e) => setWeekIndex(Number(e.currentTarget.value))}
          aria-label="Choisir une semaine"
        >
          {ACADEMIC_WEEKS.map((iso, i) => {
            const label = frWeekRange(parseLocalDate(iso));
            const holiday = weekHolidayLabel(iso);
            return (
              <option key={iso} value={String(i)}>
                {label}
                {holiday ? ` — ${holiday}` : ''}
              </option>
            );
          })}
        </select>

        <button class="btn" onClick={() => moveWeek(1)} aria-label="Semaine suivante">
          →
        </button>
        <button class="btn soft" onClick={goToTodayWeek}>
          Aujourd’hui
        </button>

        <span class="weekRange">{frWeekRange(monday)}</span>
        {holidayLabel && <span class="quickBadge">{holidayLabel}</span>}
      </div>

      <div class="weekTools">
        <button class="btn soft" onClick={() => openModal('Préparer cette semaine', <PrepWeekModal />)}>
          ✨ Préparer cette semaine
        </button>
        <button class="btn" onClick={() => openModal('Copier un jour', <CopyDayModal />)}>
          ⧉ Copier un jour
        </button>
        <button class="btn" onClick={() => withTemplate('save')}>
          ♡ Mémoriser comme semaine type
        </button>
        <button class="btn" onClick={() => withTemplate('load')}>
          ↻ Charger ma semaine type
        </button>
        <span class="spacer" />
        <span class="quickBadge">{weekLessons.length} activités cette semaine</span>
      </div>

      <div class="weekLegend">
        {LEGEND_SUBJECTS.map(({ subject, label }) => (
          <span key={subject} class="tag" style={{ background: subjectColor(subject) }}>
            {label}
          </span>
        ))}
      </div>

      <div class="card weekScroll">
        <div class="weekGrid">
          {/* Header row */}
          <div class="head" />
          {DAYS.map((name, i) => {
            const closure = closureForDate(dates[i]!);
            return (
              <div key={name} class={closure ? 'head holidayHead' : 'head'}>
                <div class="dayName">{name.toUpperCase()}</div>
                <div class="dayDate">
                  {frDayDate(dates[i]!)}
                  {closure ? ' • congé' : ''}
                </div>
              </div>
            );
          })}

          {/* Rituals band, before the first period */}
          <div class="timeCell ritualTime">
            <div>
              <b>{RITUAL_TITLE}</b>
              <br />
              <span class="small">
                {RITUAL_START} – {RITUAL_END}
              </span>
            </div>
          </div>
          {DAYS.map((name, d) => {
            const closure = closureForDate(dates[d]!);
            if (closure) {
              return (
                <div key={`ritual-${name}`} class="cell holidayCell">
                  <div class="holidayBox">{closure.label}</div>
                </div>
              );
            }

            const lesson = lessonAt(d, -1);
            return (
              <div key={`ritual-${name}`} class="cell ritualCell">
                {lesson ? (
                  <button
                    class="lessonButton"
                    onClick={() => openLesson(lesson.id, d as DayIndex, -1)}
                  >
                    <LessonCard lesson={lesson} />
                  </button>
                ) : (
                  <button class="cellAdd ritual" onClick={() => openLesson(null, d as DayIndex, -1)}>
                    + {RITUAL_TITLE}
                  </button>
                )}
              </div>
            );
          })}

          {/* Teaching periods and breaks */}
          {SLOTS.map((s) => {
            if (s.kind === 'break') {
              return (
                <div key={s.label} class="break">
                  {s.isLunch ? '🍴' : '⚽'} {s.label}
                </div>
              );
            }

            return (
              <Fragment key={`slot-${s.slot}`}>
                <div class="timeCell">
                  <div>
                    {s.start}
                    <br />
                    <span class="small">{s.end}</span>
                  </div>
                </div>

                {DAYS.map((name, d) => {
                  const closure = closureForDate(dates[d]!);
                  if (closure) {
                    return (
                      <div key={`${s.slot}-${name}`} class="cell holidayCell">
                        <div class="holidayBox">{closure.label}</div>
                      </div>
                    );
                  }

                  const lesson = lessonAt(d, s.slot);
                  return (
                    <div key={`${s.slot}-${name}`} class="cell">
                      {lesson ? (
                        <button
                          class="lessonButton"
                          onClick={() => openLesson(lesson.id, d as DayIndex, s.slot as SlotIndex)}
                        >
                          <LessonCard lesson={lesson} />
                        </button>
                      ) : (
                        <button
                          class="cellAdd"
                          onClick={() => openLesson(null, d as DayIndex, s.slot as SlotIndex)}
                          aria-label={`Ajouter une activité ${name} ${s.start}`}
                        >
                          +
                        </button>
                      )}
                    </div>
                  );
                })}
              </Fragment>
            );
          })}
        </div>
      </div>

      <div class="schoolCalendarNote">
        Calendrier scolaire : Fédération Wallonie-Bruxelles, année {SCHOOL_YEAR_LABEL}.
      </div>
    </section>
  );
}
